<template>
    <!-- Floating global alert overlay for real-time notifications -->
    <v-snackbar
        v-model="alert.show"
        color="red"
        top
        multi-line
        :timeout="0"
        class="rounded-4"    
    >
        <div class="alert-container">
      
            <!-- Left: Content Section (Takes up 85% of layout width) -->
            <div class="alert-message">
                <div>
                    <div class="font-weight-bold">
                        Urgent System Update
                    </div> 
                    <div class="caption">
                        {{ alert.message }}
                    </div>
                </div>
            </div>
            
            <!-- Right: Action Section (Takes up 15% of layout width) -->
            <div class="alert-close">
                <v-btn 
                    dark 
                    icon 
                    small
                    outline
                    @click="alert.show = false"
                    title="Close alert"
                >
                <v-icon size="18">close</v-icon>
                </v-btn>
            </div>

        </div>
    </v-snackbar>
</template>

<script>

import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr'
import { parseToManilaTime, formatWithLocalTimezone } from '@/helpers/date-helpers'
import auth from '@/auth/auth.js'

export default {
    name: 'MaintenanceEmergencyAlert',
    
    data() {
        return {
            signalRConnection: null,
            alert: {
                show: false,
                message: ''
            }
        }
    },

    methods: {
        async initializeSignalR() {
            try {
                this.signalRConnection = new HubConnectionBuilder()
                    .withUrl(`${process.env.VUE_APP_HUB_URL}maintenance`)
                    .configureLogging(LogLevel.Information)
                    .withAutomaticReconnect()
                    .build()

                // Handle automatic re-subscription on reconnection drops
                this.signalRConnection.onreconnected(async () => {
                    await this.subscribeToGroup()
                })

                await this.signalRConnection.start()
                await this.subscribeToGroup()

                this.listenForAlerts()

            } catch (error) {
                console.error('SignalR Maintenance Initialization Failed:', error)
            }
        },

        async subscribeToGroup() {
            if (!this.signalRConnection) return

            await this.signalRConnection.invoke(
                process.env.VUE_APP_HUB_MAINTENANCE_INVOKE, 
                process.env.VUE_APP_HUB_MAINTENANCE_GROUP
            ).catch(err => console.error('Failed to subscribe to maintenance group:', err))
        },

        listenForAlerts() {
            if (!this.signalRConnection) return

            this.signalRConnection.on(process.env.VUE_APP_HUB_MAINTENANCE_ALERT, (data) => {
                const payload = Array.isArray(data) ? data : data
                this.handleEmergencyPush(payload)
            })
        },

        handleEmergencyPush(notificationItem) {
            if (!notificationItem) return

            // 1. Standardize dates to Manila time mapping
            const startInstance = parseToManilaTime(notificationItem.effectiveDate)
            const localizedStartTime = formatWithLocalTimezone(startInstance, 'h:mm A')
            
            // 2. Dynamic contextual messaging based on user authorization state
            if (auth.loggedIn()) {
                this.alert.message = `Urgent update scheduled at ${localizedStartTime}. SweldoNow will be unavailable during this period.`
            } else {
                this.alert.message = `Urgent update scheduled at ${localizedStartTime}. Portal log-ins will be briefly offline.`
            }

            this.alert.show = true

            // 3. Keep App.vue updated so the central timer machine handles redirection if threshold hits
            this.$emit('emergency-triggered', notificationItem)
        }
    },

    async mounted() {
        await this.initializeSignalR()
    },

    beforeDestroy() {
        if (this.signalRConnection) {
            this.signalRConnection.stop()
            this.signalRConnection = null
        }
    }
}
</script>
<style scoped>
.alert-container {
    display: flex;
    align-items: center; 
    justify-content: space-between; 
    width: 100%; 
    margin: 0; 
    padding: 0;
}
.alert-message {
    display: flex; 
    align-items: flex-start; 
    text-align: left; 
    width: 90%;
}
.alert-close {
    width: 10%; 
    text-align: right; 
    display: flex; 
    justify-content: flex-end;
}
</style>